// Creators ledger — every creator imported from CONTACTED_CREATORS.csv,
// filterable by name/email, platform, contact date, subscriber count, and
// whether they've replied (populated once Gmail OAuth is wired).

import { useState, type ChangeEvent } from 'react';
import { useCreators, type CreatorFilters } from '../lib/queries';

const PLATFORM_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All platforms' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'podcast', label: 'Podcast' },
  { value: 'substack', label: 'Substack' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'blog', label: 'Blog' },
  { value: 'other', label: 'Other (Twitter / IG / LI)' },
];

export function Creators() {
  const [filters, setFilters] = useState<CreatorFilters>({ limit: 200 });
  const { data, isLoading, error, isFetching } = useCreators(filters);

  const total = data && data.length > 0 ? data[0]?.total_count ?? data.length : 0;
  const showing = data?.length ?? 0;

  function update<K extends keyof CreatorFilters>(key: K, value: CreatorFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function clearAll() {
    setFilters({ limit: 200 });
  }

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section B</span>
          <span>Creators</span>
          <span>{total.toLocaleString()} contacted</span>
        </div>
        <h1 className="page__title">Creators</h1>
        <p className="page__deck">
          Everyone we've reached out to. Migrated from <code>CONTACTED_CREATORS.csv</code>.
          Reply state populates once Gmail polling lands.
        </p>
      </header>

      <section className="filters">
        <div className="filters__row">
          <label className="filter">
            <span className="filter__label">Search name / email / handle</span>
            <input
              type="search"
              className="filter__input"
              value={filters.search ?? ''}
              placeholder="aswath, @ben..."
              onChange={(e: ChangeEvent<HTMLInputElement>) => update('search', e.target.value)}
            />
          </label>
          <label className="filter">
            <span className="filter__label">Platform</span>
            <select
              className="filter__select"
              value={filters.platform ?? ''}
              onChange={(e) => update('platform', e.target.value)}
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="filter">
            <span className="filter__label">Replied</span>
            <select
              className="filter__select"
              value={filters.replied ?? ''}
              onChange={(e) => update('replied', e.target.value as CreatorFilters['replied'])}
            >
              <option value="">Any</option>
              <option value="true">Has replied</option>
              <option value="false">No reply yet</option>
            </select>
          </label>
        </div>

        <div className="filters__row">
          <label className="filter">
            <span className="filter__label">Contacted from</span>
            <input
              type="date"
              className="filter__input"
              value={filters.date_from ?? ''}
              onChange={(e) => update('date_from', e.target.value)}
            />
          </label>
          <label className="filter">
            <span className="filter__label">Contacted to</span>
            <input
              type="date"
              className="filter__input"
              value={filters.date_to ?? ''}
              onChange={(e) => update('date_to', e.target.value)}
            />
          </label>
          <label className="filter">
            <span className="filter__label">Min subscribers</span>
            <input
              type="number"
              min={0}
              step={1000}
              className="filter__input"
              value={filters.min_subs ?? ''}
              placeholder="0"
              onChange={(e) => update('min_subs', e.target.value)}
            />
          </label>
          <label className="filter">
            <span className="filter__label">Max subscribers</span>
            <input
              type="number"
              min={0}
              step={1000}
              className="filter__input"
              value={filters.max_subs ?? ''}
              placeholder="—"
              onChange={(e) => update('max_subs', e.target.value)}
            />
          </label>
          <button type="button" className="filter__clear" onClick={clearAll}>
            clear
          </button>
        </div>
        <p className="filters__note">
          Subscriber data is empty for migrated rows — populates when enrichment runs (next phase).
        </p>
      </section>

      <section className="creators">
        {isLoading && <p className="creators__loading">loading…</p>}
        {error && <p className="creators__error">Failed: {error.message}</p>}
        {data && data.length === 0 && (
          <p className="creators__empty">
            No creators match these filters. <button type="button" onClick={clearAll}>Clear filters</button>
          </p>
        )}
        {data && data.length > 0 && (
          <>
            <div className="creators__count">
              <span>
                Showing <strong>{showing}</strong> of <strong>{total.toLocaleString()}</strong>
              </span>
              {isFetching && <span className="creators__refetching">refreshing…</span>}
            </div>
            <ul className="creator-list">
              {data.map((c) => (
                <li key={c.id} className="creator-row">
                  <div className="creator-row__main">
                    <h3 className="creator-row__name">
                      {c.canonical_name}
                      {c.handle && <span className="creator-row__handle">{c.handle}</span>}
                    </h3>
                    <a
                      href={`mailto:${c.email}`}
                      className="creator-row__email"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {c.email}
                    </a>
                  </div>
                  <div className="creator-row__meta">
                    <span className={`creator-row__platform creator-row__platform--${c.platform ?? 'other'}`}>
                      {c.platform ?? 'other'}
                    </span>
                    {c.profile_url && (
                      <a
                        href={c.profile_url}
                        target="_blank"
                        rel="noreferrer"
                        className="creator-row__url"
                        onClick={(e) => e.stopPropagation()}
                      >
                        profile ↗
                      </a>
                    )}
                  </div>
                  <div className="creator-row__sig">
                    <span className="creator-row__date">
                      {c.last_contacted_at
                        ? new Date(c.last_contacted_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </span>
                    <span className="creator-row__subs">
                      {c.subscriber_count != null
                        ? `${c.subscriber_count.toLocaleString()} subs`
                        : '— subs'}
                    </span>
                    {c.has_replied ? (
                      <span className="creator-row__replied creator-row__replied--yes">
                        replied · {c.reply_count}
                      </span>
                    ) : (
                      <span className="creator-row__replied creator-row__replied--no">no reply</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {showing < total && (
              <p className="creators__more">
                {total - showing} more match these filters. Increase Min/Max or refine search to find specific creators.
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
